import { useState, useRef } from 'react';
import { useToast } from './useToast';
import { apiUtils } from './useApi';
import { API_ENDPOINTS } from '../services/apiEndpoints';

// Interface definitions
interface ImportResponse {
  success: boolean;
  message: string;
  data: {
    jobId: string;
    import_id: string;
    attachment_id: string;
  };
}

interface FileState {
  file: File;
  progress: number;
  status: 'idle' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface UseImportReturn {
  // State
  selectedFile: FileState | null;
  isDragOver: boolean;
  jobResult: ImportResponse['data'] | null;
  isUploading: boolean;
  canUpload: boolean;

  // Refs
  fileInputRef: React.RefObject<HTMLInputElement | null>;

  // Handlers
  handleFileSelect: (files: FileList | null) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleBrowseClick: () => void;
  handleBrowseLinkClick: (e: React.MouseEvent) => void;
  handleRemoveFile: () => void;
  handleUpload: () => Promise<void>;

  // Utilities
  formatFileSize: (bytes: number) => string;
  validateFile: (file: File) => boolean;
}

export const useImport = (): UseImportReturn => {
  const [selectedFile, setSelectedFile] = useState<FileState | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [jobResult, setJobResult] = useState<ImportResponse['data'] | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success, error } = useToast();

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): boolean => {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
      'application/json', // .json
    ];

    if (file.size > maxSize) {
      error(
        `File size exceeds 50MB limit. Current size: ${formatFileSize(
          file.size
        )}`
      );
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      error('Please select a valid file format (Excel, CSV, or JSON)');
      return false;
    }

    return true;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!validateFile(file)) return;

    setSelectedFile({
      file,
      progress: 0,
      status: 'idle',
    });

    setJobResult(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleBrowseLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to parent UploadZone
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setJobResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile.file);

    setSelectedFile(prev =>
      prev ? { ...prev, status: 'uploading', progress: 0 } : null
    );

    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotels.import,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setSelectedFile(prev => (prev ? { ...prev, progress } : null));
            }
          },
        }
      );

      const responseData = response.data as ImportResponse;

      if (responseData.success) {
        setSelectedFile(prev =>
          prev ? { ...prev, status: 'success', progress: 100 } : null
        );
        setJobResult(responseData.data);
        success('File uploaded successfully! Processing has started.');
      } else {
        throw new Error(responseData.message || 'Upload failed');
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Upload failed';
      setSelectedFile(prev =>
        prev
          ? {
              ...prev,
              status: 'error',
              progress: 0,
              error: errorMessage,
            }
          : null
      );
      error(errorMessage);
    }
  };

  const isUploading = selectedFile?.status === 'uploading';
  const canUpload = Boolean(selectedFile && selectedFile.status === 'idle');

  return {
    // State
    selectedFile,
    isDragOver,
    jobResult,
    isUploading,
    canUpload,

    // Refs
    fileInputRef,

    // Handlers
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleBrowseClick,
    handleBrowseLinkClick,
    handleRemoveFile,
    handleUpload,

    // Utilities
    formatFileSize,
    validateFile,
  };
};

export default useImport;
